/**
 * Internationalization (i18n) module
 *
 * Stateless translation with locale fallback chains.
 *
 * Locale fallback chain:
 *   en-US  →  en  →  key
 *   de     →  en  →  key
 *   fr     →  en  →  key
 *   ...
 *
 * Regional variants (currently only en-US) only need to contain *overrides* —
 * any key missing from the variant is resolved from its base locale (en).
 */

import enTranslations from "@/i18n/en.json";
import enUSTranslations from "@/i18n/en-US.json";
import deTranslations from "@/i18n/de.json";
import frTranslations from "@/i18n/fr.json";
import esTranslations from "@/i18n/es.json";
import itTranslations from "@/i18n/it.json";
import ptTranslations from "@/i18n/pt.json";

type TranslationRecord = Record<string, string>;

const translations: Record<string, TranslationRecord> = {
  en: enTranslations,
  "en-US": enUSTranslations as TranslationRecord,
  de: deTranslations,
  fr: frTranslations,
  es: esTranslations,
  it: itTranslations,
  pt: ptTranslations,
};

/** Maps a locale to the chain of locales to consult, in order. */
const FALLBACK_CHAIN: Record<string, string[]> = {
  en: ["en"],
  "en-US": ["en-US", "en"],
  de: ["de", "en"],
  fr: ["fr", "en"],
  es: ["es", "en"],
  it: ["it", "en"],
  pt: ["pt", "en"],
};

const DEFAULT_LOCALE = "en";

/**
 * Interpolates {{variable}} placeholders in a template string with context values.
 */
function interpolate(template: string, context: Record<string, any>): string {
  return template.replace(/\{\{(\w+)\}\}/g, (_, key) => {
    const value = context[key];
    return value !== undefined ? String(value) : `{{${key}}}`;
  });
}

/**
 * Translates a message key using the locale's fallback chain.
 */
export function t(
  key: string,
  context?: Record<string, any>,
  locale: string = DEFAULT_LOCALE
): string {
  const chain = FALLBACK_CHAIN[locale] ?? [locale, DEFAULT_LOCALE];
  let template: string | undefined;
  for (const loc of chain) {
    template = translations[loc]?.[key];
    if (template) break;
  }
  if (!template) return key;

  if (context && Object.keys(context).length > 0) {
    return interpolate(template, context);
  }
  return template;
}

/**
 * Returns the list of available locales.
 */
export function getAvailableLocales(): string[] {
  return Object.keys(translations);
}

/**
 * Checks if a translation key exists in a given locale (no fallback).
 */
export function hasTranslation(key: string, locale: string = DEFAULT_LOCALE): boolean {
  return key in (translations[locale] || {});
}
